import { rest } from '@0xtorch/core'
import type { ZodType, ZodTypeDef } from 'zod'

export type KaiascanOapiClient = {
  get: <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >(parameters: {
    path: string
    query: Record<string, string>
    resultSchema: ZodType<Output, Def, Input>
    headers?: Record<string, string>
  }) => Promise<Output>
}

type CreateKaiascanOapiClientParameters = {
  oapiBaseUrl?: string
  apiKey?: string
  proxyUrl?: string
}

const defaultOapiBaseUrl = 'https://mainnet-oapi.kaiascan.io'
const rateLimitPerSecond = 5

/**
 * debug 出力用に API key をマスクした URL を作成する。
 *
 * @param url - request URL。
 * @returns API key を含まない URL 文字列。
 */
const toDebugUrl = ({ url }: { url: URL }): string => {
  const debugUrl = new URL(url.toString())
  if (debugUrl.searchParams.has('apikey')) {
    debugUrl.searchParams.set('apikey', '[REDACTED]')
  }
  return debugUrl.toString()
}

/**
 * Kaiascan OAPI client を作成する。
 *
 * @param parameters - OAPI base URL、API key、proxy URL。
 * @returns Kaiascan OAPI client。
 */
export const createKaiascanOapiClient = ({
  oapiBaseUrl = defaultOapiBaseUrl,
  apiKey,
  proxyUrl,
}: CreateKaiascanOapiClientParameters): KaiascanOapiClient => {
  const requestTimestamps: number[] = []

  const get = async <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
  >(
    {
      path,
      query,
      resultSchema,
      headers,
    }: {
      path: string
      query: Record<string, string>
      resultSchema: ZodType<Output, Def, Input>
      headers?: Record<string, string>
    },
    repeat = 0,
  ): Promise<Output> => {
    const time1SecondAgo = Date.now() - 1000
    if (
      requestTimestamps.filter((t) => t >= time1SecondAgo).length >=
      rateLimitPerSecond
    ) {
      await new Promise<void>((resolve) => {
        const intervalId = setInterval(() => {
          const refreshedTime1SecondAgo = Date.now() - 1000
          if (
            requestTimestamps.filter((t) => t >= refreshedTime1SecondAgo)
              .length < rateLimitPerSecond
          ) {
            clearInterval(intervalId)
            resolve()
          }
        }, 1000)
      })
    }
    requestTimestamps.push(Date.now())

    try {
      const url = new URL(path, oapiBaseUrl)
      for (const [key, value] of Object.entries(query)) {
        url.searchParams.set(key, value)
      }
      if (apiKey !== undefined) {
        url.searchParams.set('apikey', apiKey)
      }
      console.debug(`[GET] ${toDebugUrl({ url })}`)
      return await rest(
        proxyUrl === undefined
          ? url.toString()
          : `${proxyUrl}${encodeURIComponent(url.toString())}`,
        {
          schema: resultSchema,
          fetchOptions: {
            headers: {
              ...headers,
            },
          },
        },
      )
    } catch (error) {
      if (repeat < 3 && error instanceof Error) {
        if (
          error.message === 'Max rate limit reached' ||
          error.message === 'Max calls per sec rate limit reached' ||
          error.message === 'Max calls per sec rate limit reached (5/sec)' ||
          error.message ===
            'Max rate limit reached, please use API Key for higher rate limit'
        ) {
          console.debug(error)
          await new Promise((resolve) => setTimeout(resolve, 5000))
          return get({ path, query, resultSchema, headers }, repeat + 1)
        }
        if (
          error.message.includes('429') ||
          error.message.includes(
            'Unexpected error, timeout or server too busy. Please try again later',
          )
        ) {
          console.debug(error)
          await new Promise((resolve) => setTimeout(resolve, 10_000))
          return get({ path, query, resultSchema, headers }, repeat + 1)
        }
      }
      throw error
    }
  }

  return {
    get,
  }
}
